import {css} from '@mui/material';
import {PopupWrapper} from '@wallaby-ui/core';
import React, {useCallback, useState} from 'react';
import {useIntl} from 'react-intl';

import {formatDayMonth} from './alert_format';
import {AlertEpisodeAction, AlertEpisodeDispositionPostDto, AlertSnoozeOptionDto} from './alert_types';
import {LightPanelActionButton, LightPanelActionTone} from '../LightPanelActionButton';

type Props = {
  actions: AlertEpisodeAction[];
  /** The five durations with their dates, computed by the BE; the menu renders them and never derives a date. */
  snoozeOptions: AlertSnoozeOptionDto[];
  /** Called when the snooze menu opens and no options are known yet (the panel may fetch them lazily). */
  onSnoozeOptionsNeeded?: () => void;
  onAction: (post: AlertEpisodeDispositionPostDto) => void;
  disabled?: boolean;
  /** 'panel' spaces the buttons 5px apart, 'list' 6px as the Alerts Table design. */
  variant?: 'panel' | 'list';
};

/** Which disposition each action posts. Restore and un-snooze both send NONE (BE contract). */
const ACTION_POSTS: Record<Exclude<AlertEpisodeAction, 'SNOOZE'>, AlertEpisodeDispositionPostDto> = {
  SOLVE: {disposition: 'SOLVED'},
  DELETE: {disposition: 'DELETED'},
  UNSNOOZE: {disposition: 'NONE'},
  RESTORE: {disposition: 'NONE'},
};
/** The order the design draws the buttons in. */
const ACTION_ORDER: AlertEpisodeAction[] = ['SOLVE', 'SNOOZE', 'DELETE', 'UNSNOOZE', 'RESTORE'];
const ACTION_ICONS: Record<AlertEpisodeAction, {iconName: string; viewBox: string}> = {
  SOLVE: {iconName: 'checkSolidIcon', viewBox: '0 0 448 512'},
  SNOOZE: {iconName: 'clockIcon', viewBox: '0 0 512 512'},
  DELETE: {iconName: 'trashCanIcon', viewBox: '0 0 448 512'},
  UNSNOOZE: {iconName: 'rotateLeftIcon', viewBox: '0 0 512 512'},
  RESTORE: {iconName: 'rotateLeftIcon', viewBox: '0 0 512 512'},
};
const ACTION_TONES: Record<AlertEpisodeAction, LightPanelActionTone> = {
  SOLVE: 'positive',
  SNOOZE: 'snooze',
  DELETE: 'neutral',
  UNSNOOZE: 'positive',
  RESTORE: 'positive',
};

const edgeCss = css`
  display: flex;
  gap: 5px;
  align-items: center;
  flex: none;
  &[data-variant='list'] {
    gap: 6px;
  }
`;
/* the snooze menu: `.menu` of the design */
const menuCss = css`
  width: 186px;
  display: flex;
  flex-direction: column;
  cursor: default;
`;
const menuHeaderCss = css`
  font-family: var(--dlFontFamilySemiBold);
  font-size: 10.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--dlThemeGray-300);
  padding: 7px 10px 5px;
`;
const menuItemCss = css`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 8px;
  font-family: var(--dlFontFamilySemiBold);
  font-size: 13.5px;
  color: var(--dlThemeGray-700);
  cursor: pointer;
  &:hover {
    background: var(--dlThemeGray-50);
  }
`;
const menuDateCss = css`
  margin-left: auto;
  font-family: var(--dlFontFamilyNormal);
  font-size: 11.5px;
  color: var(--dlThemeGray-300);
`;

/**
 * The right-edge action buttons of an episode row (PDW-182): Solve, Snooze, Delete on an active episode,
 * Un-snooze on a snoozed one, Restore on a solved or deleted one while it still matches. Which buttons exist
 * is the BE's `availableActions`; the FE never re-implements the state machine. Snooze opens a menu
 * "Snooze for…" with 1/3/5/7/14 days, each with its resulting date. No confirmation dialogs anywhere.
 */
export const AlertEpisodeActions: React.FC<Props> = (props) => {
  const {actions, snoozeOptions, onSnoozeOptionsNeeded, onAction, disabled, variant = 'panel'} = props;
  const intl = useIntl();
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);
  const openSnooze = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!snoozeOptions.length && onSnoozeOptionsNeeded) onSnoozeOptionsNeeded();
      setSnoozeAnchor(event.currentTarget);
    },
    [snoozeOptions.length, onSnoozeOptionsNeeded]
  );
  const closeSnooze = useCallback(() => setSnoozeAnchor(null), []);
  const stopPropagation = useCallback((event: React.SyntheticEvent) => event.stopPropagation(), []);
  if (!actions.length) return null;
  const ordered = ACTION_ORDER.filter((a) => actions.includes(a));
  return (
    // data-menu-open: the snooze menu is portalled out of the row, so the row loses its hover and focus while the user
    // is in it; the row keeps the buttons shown by this flag, the menu still pointing at its button
    <div css={edgeCss} data-name={'dl-alert-episode-actions'} data-variant={variant} data-menu-open={Boolean(snoozeAnchor)}>
      {ordered.map((action) => {
        const {iconName, viewBox} = ACTION_ICONS[action];
        const isOpenSnooze = action === 'SNOOZE' && Boolean(snoozeAnchor);
        return (
          <LightPanelActionButton
            key={action}
            iconName={iconName}
            viewBox={viewBox}
            title={intl.formatMessage({id: `alert.action.${action}`})}
            tone={ACTION_TONES[action]}
            // the design's bubble, none while the snooze menu is open
            hideTitle={isOpenSnooze}
            open={isOpenSnooze}
            disabled={disabled}
            action={action}
            dataName={'dl-alert-episode-action'}
            onClick={
              action === 'SNOOZE'
                ? openSnooze
                : (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onAction(ACTION_POSTS[action]);
                  }
            }
          />
        );
      })}
      {snoozeAnchor && (
        <PopupWrapper
          openButton={false}
          anchorTag={snoozeAnchor}
          onClose={closeSnooze}
          direction='down'
          yOffset={2}
          contentStyle={{padding: 6, gap: 0}}
          background='#fff'
          data-name={'dl-alert-snooze-menu'}
        >
          <div css={menuCss} onClick={stopPropagation} onMouseDown={stopPropagation}>
            <div css={menuHeaderCss}>{intl.formatMessage({id: 'alert.snooze.menu.title'})}</div>
            {snoozeOptions.map((option) => (
              <div
                css={menuItemCss}
                key={option.days}
                role='menuitem'
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSnoozeAnchor(null);
                    onAction({disposition: 'SNOOZED', snoozeDays: option.days});
                  }
                }}
                data-name={'dl-alert-snooze-option'}
                data-days={option.days}
                onClick={() => {
                  setSnoozeAnchor(null);
                  onAction({disposition: 'SNOOZED', snoozeDays: option.days});
                }}
              >
                {intl.formatMessage({id: 'alert.snooze.days'}, {days: option.days})}
                <span css={menuDateCss}>→ {formatDayMonth(option.expiresOn)}</span>
              </div>
            ))}
            {!snoozeOptions.length && <div css={menuItemCss}>…</div>}
          </div>
        </PopupWrapper>
      )}
    </div>
  );
};

AlertEpisodeActions.displayName = 'AlertEpisodeActions';
